import { useState, useCallback } from 'react';
import type { PermissionTree } from '../types/role.types';

interface GroupState {
	checked: boolean;
	indeterminate: boolean;
}

/**
 * Recursively collect all leaf permission values under a group node.
 */
function getAllLeafPermissions(group: PermissionTree): string[] {
	const leaves: string[] = [];

	if (group.permissions) {
		for (const perm of group.permissions) {
			leaves.push(perm.value);
		}
	}

	if (group.groups) {
		for (const subGroup of group.groups) {
			leaves.push(...getAllLeafPermissions(subGroup));
		}
	}

	return leaves;
}

/**
 * Hook for managing permission tree selection with tri-state checkbox logic.
 *
 * Only leaf permission values are stored. Group states (checked/indeterminate)
 * are always derived from the selected leaves.
 */
export function usePermissionTree(initialPermissions: string[] = []) {
	const [selected, setSelected] = useState<Set<string>>(() => new Set(initialPermissions));

	const togglePermission = useCallback((value: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(value)) {
				next.delete(value);
			} else {
				next.add(value);
			}
			return next;
		});
	}, []);

	const toggleGroup = useCallback((group: PermissionTree) => {
		const leaves = getAllLeafPermissions(group);
		setSelected((prev) => {
			const next = new Set(prev);
			const allSelected = leaves.every((v) => prev.has(v));

			if (allSelected) {
				for (const v of leaves) next.delete(v);
			} else {
				for (const v of leaves) next.add(v);
			}

			return next;
		});
	}, []);

	const getGroupState = useCallback(
		(group: PermissionTree): GroupState => {
			const leaves = getAllLeafPermissions(group);
			if (leaves.length === 0) return { checked: false, indeterminate: false };

			const selectedCount = leaves.filter((v) => selected.has(v)).length;

			if (selectedCount === 0) return { checked: false, indeterminate: false };
			if (selectedCount === leaves.length) return { checked: true, indeterminate: false };
			return { checked: false, indeterminate: true };
		},
		[selected]
	);

	const selectAll = useCallback((tree: PermissionTree[]) => {
		const allLeaves = new Set<string>();
		for (const node of tree) {
			for (const v of getAllLeafPermissions(node)) {
				allLeaves.add(v);
			}
		}
		setSelected(allLeaves);
	}, []);

	const deselectAll = useCallback(() => {
		setSelected(new Set());
	}, []);

	const setPermissions = useCallback((values: string[]) => {
		setSelected(new Set(values));
	}, []);

	return {
		selectedPermissions: selected,
		selectedArray: Array.from(selected),
		togglePermission,
		toggleGroup,
		getGroupState,
		selectAll,
		deselectAll,
		setPermissions,
	};
}
